import { useEffect, useRef, useState } from "react";
import { api, onStudioChanged, type Job } from "../api.js";

/** While something is running or waiting, the list is worth watching closely; when the studio is idle, once in a while is plenty. */
export const BUSY_MS = 2000;
export const IDLE_MS = 10_000;

type Listener = (jobs: Job[]) => void;

/**
 * One poll of the job list for the whole app. Six parts of the editor used to ask for it on their own timers, which over a slow or
 * lossy link meant six round trips competing with the video the owner is trying to watch. They all read from this instead: one
 * request, slower when nothing is happening, and stopped altogether while the window is in the background.
 */
export class JobFeed {
  jobs: Job[] = [];
  private subs = new Set<Listener>();
  private timer: ReturnType<typeof setTimeout> | null = null;
  private inFlight = false;
  constructor(private fetchJobs: () => Promise<{ jobs: Job[] }> = () => api.jobs(), private hidden: () => boolean = () => typeof document !== "undefined" && document.visibilityState === "hidden") {}

  /** How long until the next poll: quick while there is work, lazy when there is none. */
  interval(): number {
    return this.jobs.some((j) => j.status === "running" || j.status === "queued") ? BUSY_MS : IDLE_MS;
  }
  subscribe(fn: Listener): () => void {
    this.subs.add(fn);
    fn(this.jobs);
    if (this.subs.size === 1) void this.poll();
    return () => { this.subs.delete(fn); if (!this.subs.size) this.stop(); };
  }
  stop(): void { if (this.timer) clearTimeout(this.timer); this.timer = null; }
  /** Asks now, whatever the timer was doing: used right after the owner starts something. */
  async poll(): Promise<void> {
    if (this.inFlight || !this.subs.size) return;
    if (this.hidden()) { this.arm(); return; } // nobody is looking: come back later rather than talk to the studio for nothing
    this.inFlight = true;
    try {
      const r = await this.fetchJobs();
      this.jobs = r.jobs;
      for (const fn of this.subs) fn(this.jobs);
    } catch { /* a missed poll is not worth reporting; the next one will do */ }
    finally { this.inFlight = false; this.arm(); }
  }
  private arm(): void {
    this.stop();
    if (!this.subs.size) return;
    this.timer = setTimeout(() => void this.poll(), this.interval());
  }
}

export const jobFeed = new JobFeed();
// the owner just did something: look straight away rather than waiting out the idle interval
onStudioChanged(() => { void jobFeed.poll(); });
/** Ask for a fresh list now (after starting a job, so the owner sees it appear at once). */
export const refreshJobs = (): Promise<void> => jobFeed.poll();

if (typeof document !== "undefined") {
  document.addEventListener("visibilitychange", () => { if (document.visibilityState === "visible") void jobFeed.poll(); });
}

/**
 * What one part of the screen needs from the job list. The selector runs on every poll, but the component only re-renders when
 * what it picked actually changed.
 */
export function useJobs<T>(select: (jobs: Job[]) => T): T {
  const pick = useRef(select);
  pick.current = select;
  const [value, setValue] = useState<T>(() => select(jobFeed.jobs));
  const last = useRef<string>(JSON.stringify(value) ?? "");
  useEffect(() => jobFeed.subscribe((jobs) => {
    const next = pick.current(jobs);
    const key = JSON.stringify(next) ?? "";
    if (key !== last.current) { last.current = key; setValue(next); }
  }), []);
  return value;
}
